import * as React from 'react';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'react-recompose'.
import {compose} from 'react-recompose';
import {oneLineText} from '@packages/systems/design-system/constants/Styles';
import {noEmpty} from '@packages/utilities/component';
import {styled, Z_INDEXES, type Theme} from '@packages/utilities/styled';
import {emptyArray} from '@packages/utilities/fp/utils';
import {FlexRow} from '@packages/systems/design-system/Layout';
import {Clickable} from '@packages/systems/design-system/logic/clickable';
import {lighten} from '@packages/systems/core/utils/ColorUtils';

type BorderStyleArgs = {
  control: Theme['control'];
  light?: boolean;
  selected?: boolean;
  colors: Theme['colors'];
};

const getBorderStyle = ({
  control,
  light,
  selected,
  colors,
}: BorderStyleArgs) => {
  if (light) {
    return {
      border: `${control.borderWidth}px solid ${lighten(8)(colors.borderDeep)}`,
    };
  }

  if (selected) {
    return {
      border: `${control.borderWidth}px solid ${colors.accent}`,
      borderRadius: control.borderRadius,
      zIndex: Z_INDEXES.zIndexMax,
    };
  }

  return {border: `${control.borderWidth}px solid ${colors.borderDeep}`};
};

const Wrapper = styled(Clickable)(
  ({
    theme: {colors, gutters, control},
    style,
    height,
    light,
    condensed,
    selected,
  }) => ({
    display: 'flex',
    position: 'relative',
    boxSizing: 'border-box',
    height:
      height || (condensed ? control.height : control.height + gutters.narrow),
    paddingRight: gutters.narrow,
    paddingLeft: gutters.narrow,
    background: light
      ? lighten(4.5)(colors.backgroundDeepAlt)
      : colors.backgroundDeepAlt,
    ...getBorderStyle({control, light, selected, colors}),
    ...style,
  }),
  {
    first: ({theme: {control}}) => ({
      borderTopRightRadius: control.borderRadius,
      borderTopLeftRadius: control.borderRadius,
    }),
    last: ({theme: {control}}) => ({
      borderBottomRightRadius: control.borderRadius,
      borderBottomLeftRadius: control.borderRadius,
    }),
  }
);

const NoEmpty = compose(noEmpty)(
  (props: React.ComponentProps<typeof FlexRow>) => (
    <FlexRow align="center" {...props} />
  )
);

// `ListHeader` is a legacy component. Most props are typed `any` because
// callers pass through a wide range of shapes (icons, labels, indicator
// arrays, event handlers) and tightening them is out of scope for the
// styled-types rework. Pin a usable optional shape that matches how it's
// invoked in practice.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyProp = any;

export type ListHeaderProps = {
  'data-automation-id'?: string;
  icon?: AnyProp;
  label?: AnyProp;
  indicators?: ReadonlyArray<unknown>;
  extra?: AnyProp;
  light?: AnyProp;
  height?: AnyProp;
  condensed?: AnyProp;
  first?: AnyProp;
  last?: AnyProp;
  selected?: AnyProp;
  style?: AnyProp;
  onClick?: AnyProp;
  onDoubleClick?: AnyProp;
  onRightClick?: AnyProp;
  onMouseOver?: AnyProp;
  onMouseEnter?: AnyProp;
  onMouseLeave?: AnyProp;
};

const ListHeader = ({
  'data-automation-id': dataAutomationId = 'list-header',
  icon,
  label,
  indicators = emptyArray,
  extra,
  light,
  height,
  condensed,
  first,
  last,
  selected,
  style,
  onClick,
  onDoubleClick,
  onRightClick,
  onMouseOver,
  onMouseEnter,
  onMouseLeave,
}: ListHeaderProps) => (
  <Wrapper
    data-automation-id={dataAutomationId}
    style={style}
    selected={selected}
    first={first}
    last={last}
    light={light}
    condensed={condensed}
    height={height}
    onContextMenu={onRightClick}
    onClick={onClick}
    onDoubleClick={onDoubleClick}
    onMouseOver={onMouseOver}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    <NoEmpty>{icon}</NoEmpty>
    <FlexRow grow={1} align="center" style={oneLineText}>
      {label}

      <NoEmpty>{indicators}</NoEmpty>
    </FlexRow>
    <NoEmpty>{extra}</NoEmpty>
  </Wrapper>
);

export default React.memo(ListHeader);
